import { useMemo, useState, type CSSProperties, type FC } from 'react';
import type {
  CalendarTabKey,
  ProjectMemberCalendarEvent,
  ProjectMemberProfile,
} from '../types';

interface ProjectMemberCalendarOverlayProps {
  isOpen: boolean;
  profile: ProjectMemberProfile | null;
  events: ProjectMemberCalendarEvent[];
  onClose: () => void;
  onCreateEvent: (payload: Partial<ProjectMemberCalendarEvent>) => Promise<void>;
  onDeleteEvent: (eventId: string) => Promise<void>;
}

function eventStatusAccent(status: string): string {
  if (status === 'CRITICAL') return '#ff7b65';
  return '#49ddb7';
}

function eventStatusLabel(status: string): string {
  if (status === 'CRITICAL') return 'Критично';
  if (status === 'CONFIRMED') return 'Подтверждено';
  if (status === 'ACTIVE') return 'Активно';
  return status;
}

/** Service calendar — cab-overlay chrome + cabinet-* body. */
export const ProjectMemberCalendarOverlay: FC<ProjectMemberCalendarOverlayProps> = ({
  isOpen,
  profile,
  events,
  onClose,
  onCreateEvent,
  onDeleteEvent,
}) => {
  const [activeTab, setActiveTab] = useState<CalendarTabKey>('TODAY');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [eventTitle, setEventTitle] = useState('');
  const [eventKind, setEventKind] = useState('DEADLINE');
  const [eventStartsAt, setEventStartsAt] = useState('');
  const [eventProject, setEventProject] = useState('PRJ-DEMO-004');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (activeTab === 'DEADLINES' && e.kind !== 'DEADLINE') return false;
      if (activeTab === 'MEETINGS' && e.kind !== 'MEETING') return false;
      if (activeTab === 'PUBLICATIONS' && e.kind !== 'PUBLICATION') return false;
      if (activeTab === 'PROJECTS' && e.kind !== 'PROJECT') return false;
      if (activeTab === 'FOCUS' && e.kind !== 'FOCUS') return false;
      return true;
    });
  }, [events, activeTab]);

  if (!isOpen) return null;

  const handleCreateSubmit = async () => {
    if (!eventTitle.trim()) return;
    setIsSubmitting(true);
    try {
      await onCreateEvent({
        title: eventTitle.trim(),
        kind: eventKind,
        starts_at: eventStartsAt || new Date().toISOString().slice(0, 16).replace('T', ' '),
        project_code: eventProject || undefined,
        status: eventKind === 'DEADLINE' ? 'CRITICAL' : 'CONFIRMED',
      });
      setEventTitle('');
      setEventStartsAt('');
      setIsCreateModalOpen(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const navItems = [
    { key: 'TODAY', label: 'Сегодня', count: 6 },
    { key: 'TIMELINE', label: 'Таймлайн', count: 6 },
    { key: 'DEADLINES', label: 'Дедлайны', count: 3 },
    { key: 'MEETINGS', label: 'Встречи / дежурство', count: 2 },
    { key: 'PUBLICATIONS', label: 'Публикации', count: 1 },
    { key: 'PROJECTS', label: 'Проекты / гранты', count: 1 },
    { key: 'FOCUS', label: 'Фокус-план', count: null as number | null },
    { key: 'AVAILABILITY', label: 'Доступность', count: 5 },
    { key: 'ARCHIVE', label: 'Архив', count: 1 },
  ];

  return (
    <div className="cab-overlay" role="dialog" aria-modal="true">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="cab-top">
          <div className="cab-top-left">
            <div>
              <small>Календарь операций</small>
              <h2>Служебный календарь</h2>
              <p>Дедлайны, встречи, публикации, проекты и время на исследование</p>
            </div>
          </div>
          <div className="cab-top-actions">
            <button
              type="button"
              className="cab-btn primary"
              onClick={() => setIsCreateModalOpen(true)}
            >
              + Новое событие
            </button>
            <button type="button" className="cab-btn icon" onClick={onClose} aria-label="Закрыть">
              ✕
            </button>
          </div>
        </div>

        <div className="cab-body" style={{ display: 'grid', gridTemplateColumns: '220px minmax(0, 1fr) 240px', gap: 14 }}>
          <aside className="cabinet-panel">
            <header>
              <div>
                <h3>Рабочие пространства</h3>
              </div>
            </header>
            <div className="cabinet-panel-body">
              <div className="cabinet-filters" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                {navItems.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    className={`cabinet-filter${activeTab === item.key ? ' active' : ''}`}
                    style={{ justifyContent: 'space-between', display: 'flex', borderRadius: 8 }}
                    onClick={() => setActiveTab(item.key as CalendarTabKey)}
                  >
                    <span>{item.label}</span>
                    {item.count !== null ? <span>{item.count}</span> : null}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <main className="cabinet-stack">
            <section className="cabinet-hero" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
              <div>
                <div className="cabinet-eyebrow">Центр дня</div>
                <h2>Сегодня · {profile?.full_name || 'Участник проекта'}</h2>
                <p>
                  Календарь показывает не просто даты, а следующий осмысленный шаг: что подготовить,
                  с кем согласовать и к какому объекту относится событие.
                </p>
              </div>
              <button
                type="button"
                className="cabinet-btn primary"
                onClick={() => setIsCreateModalOpen(true)}
              >
                + Событие
              </button>
            </section>

            <section className="cabinet-kpis">
              <article className="cabinet-kpi" style={{ '--accent': '#8faebb' } as CSSProperties}>
                <small>Активные события</small>
                <strong>6</strong>
              </article>
              <article className="cabinet-kpi" style={{ '--accent': '#ff7b65' } as CSSProperties}>
                <small>Требуют внимания</small>
                <strong>3</strong>
              </article>
              <article className="cabinet-kpi" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                <small>Встречи / СФНА</small>
                <strong>2</strong>
              </article>
              <article className="cabinet-kpi" style={{ '--accent': '#49ddb7' } as CSSProperties}>
                <small>Публикации</small>
                <strong>1</strong>
              </article>
            </section>

            <section>
              <div className="cabinet-actions" style={{ marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 15 }}>Следующие действия</h3>
                <span className="cabinet-spacer" />
                <span style={{ fontSize: 12, color: '#8faebb' }}>
                  Приоритет рассчитывается из срока, статуса и связанного объекта
                </span>
              </div>
              <div className="cabinet-grid2">
                {filteredEvents.map((evt) => (
                  <article
                    key={evt.id}
                    className="cabinet-card"
                    style={{ '--accent': eventStatusAccent(evt.status) } as CSSProperties}
                  >
                    <div className="cabinet-actions">
                      <span style={{ fontSize: 12, color: '#e2ad3a', fontWeight: 700 }}>
                        {evt.starts_at}
                      </span>
                      <span
                        className="cabinet-badge"
                        style={{ '--badge': eventStatusAccent(evt.status) } as CSSProperties}
                      >
                        {eventStatusLabel(evt.status)}
                      </span>
                    </div>
                    <strong style={{ display: 'block', marginTop: 8 }}>{evt.title}</strong>
                    {evt.description ? (
                      <p style={{ margin: '6px 0 0', color: '#8faebb', fontSize: 12 }}>
                        {evt.description}
                      </p>
                    ) : null}
                    <div className="cabinet-actions" style={{ marginTop: 10 }}>
                      <span style={{ fontSize: 11, color: '#8faebb' }}>
                        {evt.project_code || 'PRJ-DEMO-004'}
                      </span>
                      <span className="cabinet-spacer" />
                      <button
                        type="button"
                        className="cabinet-btn"
                        onClick={() => void onDeleteEvent(evt.id)}
                      >
                        Удалить
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            </section>

            <section className="cabinet-card" style={{ '--accent': '#41d2ff' } as CSSProperties}>
              <h3 style={{ margin: '0 0 10px', fontSize: 15 }}>Пульс недели</h3>
              <div className="cabinet-kpis">
                <article className="cabinet-kpi" style={{ '--accent': '#ff7b65' } as CSSProperties}>
                  <small>Нагрузка 72ч</small>
                  <strong>3</strong>
                  <span>критических / высоких событий</span>
                </article>
                <article className="cabinet-kpi" style={{ '--accent': '#41d2ff' } as CSSProperties}>
                  <small>Встречи</small>
                  <strong>2</strong>
                  <span>встречи и консультации</span>
                </article>
                <article className="cabinet-kpi" style={{ '--accent': '#49ddb7' } as CSSProperties}>
                  <small>Глубокая работа</small>
                  <strong>90</strong>
                  <span>минут защищённого времени</span>
                </article>
                <article className="cabinet-kpi" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
                  <small>Связано с объектами</small>
                  <strong>6</strong>
                  <span>событий без дублирования</span>
                </article>
              </div>
            </section>
          </main>

          <aside className="cabinet-stack">
            <article className="cabinet-card" style={{ '--accent': '#49ddb7' } as CSSProperties}>
              <div className="cabinet-eyebrow">Подсказки календаря</div>
              <p style={{ margin: '8px 0 0', color: '#8faebb', fontSize: 12, lineHeight: 1.5 }}>
                Проверьте предложенные сроки, пересечения и свободное время перед согласованием встречи.
              </p>
              <div className="cabinet-stack" style={{ marginTop: 10 }}>
                <button type="button" className="cabinet-btn" style={{ width: '100%' }}>
                  Пересобрать день по приоритетам
                </button>
                <button type="button" className="cabinet-btn" style={{ width: '100%' }}>
                  Найти конфликтующие сроки
                </button>
                <button type="button" className="cabinet-btn" style={{ width: '100%' }}>
                  Защитить время на исследование
                </button>
              </div>
            </article>

            <article className="cabinet-card" style={{ '--accent': '#e2ad3a' } as CSSProperties}>
              <h4 style={{ margin: '0 0 8px', fontSize: 13 }}>Ближайшие 3</h4>
              <div className="cabinet-stack">
                <div className="cabinet-note">
                  <span style={{ fontSize: 11, color: '#8faebb' }}>14.08.2026 · 09:00</span>
                  <strong style={{ display: 'block', marginTop: 4 }}>
                    Владелец: рабочая встреча
                  </strong>
                </div>
                <div className="cabinet-note" style={{ borderColor: 'rgba(255,123,101,.35)' }}>
                  <span style={{ fontSize: 11, color: '#ff7b65' }}>15.08.2026 · 18:00</span>
                  <strong style={{ display: 'block', marginTop: 4 }}>Контрольный дедлайн</strong>
                </div>
                <div className="cabinet-note">
                  <span style={{ fontSize: 11, color: '#8faebb' }}>16.08.2026 · 11:30</span>
                  <strong style={{ display: 'block', marginTop: 4 }}>
                    Публикационная веха
                  </strong>
                </div>
              </div>
            </article>
          </aside>
        </div>
      </div>

      {isCreateModalOpen ? (
        <div className="cabinet-modal-backdrop" role="dialog" aria-modal="true">
          <div className="cabinet-modal" style={{ maxWidth: 560 }}>
            <header className="cabinet-modal-head">
              <div>
                <h2>Новое событие</h2>
              </div>
              <button
                type="button"
                className="cabinet-btn"
                onClick={() => setIsCreateModalOpen(false)}
                aria-label="Закрыть"
              >
                ✕
              </button>
            </header>
            <div className="cabinet-modal-body">
              <div className="cabinet-stack">
                <label className="cabinet-field">
                  <span>Название события</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    placeholder="Например: Встреча с научным руководителем"
                  />
                </label>
                <div className="cabinet-grid2">
                  <label className="cabinet-field">
                    <span>Тип</span>
                    <select
                      className="cabinet-select"
                      value={eventKind}
                      onChange={(e) => setEventKind(e.target.value)}
                    >
                      <option value="DEADLINE">Дедлайн</option>
                      <option value="MEETING">Встреча</option>
                      <option value="PUBLICATION">Публикация</option>
                      <option value="PROJECT">Проект</option>
                      <option value="FOCUS">Фокус-время</option>
                    </select>
                  </label>
                  <label className="cabinet-field">
                    <span>Дата и время</span>
                    <input
                      className="cabinet-input"
                      type="text"
                      value={eventStartsAt}
                      onChange={(e) => setEventStartsAt(e.target.value)}
                      placeholder="18.08.2026 · 14:00"
                    />
                  </label>
                </div>
                <label className="cabinet-field">
                  <span>Код проекта / задачи</span>
                  <input
                    className="cabinet-input"
                    type="text"
                    value={eventProject}
                    onChange={(e) => setEventProject(e.target.value)}
                    placeholder="PRJ-DEMO-004"
                  />
                </label>
                <div className="cabinet-actions" style={{ justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="cabinet-btn"
                    onClick={() => setIsCreateModalOpen(false)}
                  >
                    Отмена
                  </button>
                  <button
                    type="button"
                    className="cabinet-btn primary"
                    disabled={isSubmitting || !eventTitle.trim()}
                    onClick={() => void handleCreateSubmit()}
                  >
                    Создать
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};
